import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardBody, Button } from "@heroui/react";
import { ArrowUpDown } from "lucide-react";
import { motion } from "framer-motion";
import { useWallet } from "@solana/wallet-adapter-react";
import { logout } from "@multiversx/sdk-dapp/utils";
import { useGetAccountInfo } from "@/hooks/sdkDappHooks";
import {
  NetworkType,
  BridgeWalletModal,
  useBridgeWallet,
} from "@/components/BridgeWallet";
import { useGetA1XBalance } from "@/hooks/useGetAccountTokens";
import { useSolanaTokenBalance } from "@/hooks/useSolanaTokenBalance";
import { useBridgeEstimation } from "@/hooks/useBridgeEstimation";
import { useBridgeLimits } from "@/hooks/useBridgeLimits";
import { useBridgeSwap } from "@/hooks/useBridgeSwap";
import { useBridgeTransactionHooks } from "@/hooks/useBridgeTransactionHooks";
import { formatChainIdForTradesilvania } from "@/utils/bridge";
import { toast } from "sonner";
import { useCountdown } from "@/hooks/useCountdown";
import { BridgeHeader } from "@/components/Bridge/BridgeHeader";
import { BridgeNetworkCard } from "@/components/Bridge/BridgeNetworkCard";
// import { BridgeHistoryCard } from "@/components/Bridge/BridgeHistoryCard";

interface SwapDirection {
  from: NetworkType;
  to: NetworkType;
}

export const Bridge = () => {
  const [amount, setAmount] = useState("");
  const [swapDirection, setSwapDirection] = useState<SwapDirection>({
    from: "solana",
    to: "multiversx",
  });

  // Bridge wallet system
  const bridgeWallet = useBridgeWallet();

  // Wallet access
  const solanaWallet = useWallet();
  const { publicKey: solanaPublicKey, disconnect: disconnectSolana } =
    solanaWallet;
  const { address: mvxAddress } = useGetAccountInfo();

  // Balances
  const { data: a1xBalance, isLoading: isLoadingMvxBalance } =
    useGetA1XBalance();
  const { data: solanaA1XBalance, isLoading: isLoadingSolanaBalance } =
    useSolanaTokenBalance();

  // Memoized helpers
  const getA1XBalance = useCallback(
    (network: NetworkType): string => {
      if (network === "multiversx") {
        return a1xBalance?.formattedBalance || "0";
      }
      return solanaA1XBalance?.formattedBalance || "0";
    },
    [a1xBalance?.formattedBalance, solanaA1XBalance?.formattedBalance]
  );

  const isBalanceLoading = useCallback(
    (network: NetworkType): boolean => {
      if (network === "multiversx") {
        return isLoadingMvxBalance;
      }
      return isLoadingSolanaBalance;
    },
    [isLoadingMvxBalance, isLoadingSolanaBalance]
  );

  const handleDisconnect = useCallback(
    (network: NetworkType) => {
      if (network === "solana") {
        disconnectSolana();
      } else {
        logout();
      }
    },
    [disconnectSolana]
  );

  // Bridge data
  const { data: limits, error: limitsError } = useBridgeLimits({
    fromNetwork: swapDirection.from,
    toNetwork: swapDirection.to,
  });

  // Validation
  const validationState = useMemo(() => {
    const fromBalance = parseFloat(getA1XBalance(swapDirection.from) || "0");
    const amountValue = parseFloat(amount || "0");
    const minAmount = limits ? parseFloat(limits.assetFrom.amountMin) : 0;
    const maxAmount = limits
      ? parseFloat(limits.assetFrom.amountMax)
      : Number.MAX_SAFE_INTEGER;

    return {
      hasInsufficientBalance: amountValue > 0 && amountValue > fromBalance,
      isBelowMinimum: amountValue > 0 && amountValue < minAmount,
      isAboveMaximum: amountValue > 0 && amountValue > maxAmount,
      minAmount,
      maxAmount,
    };
  }, [amount, getA1XBalance, swapDirection.from, limits]);

  const {
    hasInsufficientBalance,
    isBelowMinimum,
    isAboveMaximum,
    minAmount,
    maxAmount,
  } = validationState;

  // Bridge estimation
  const {
    data: estimation,
    isLoading: isEstimating,
    error: estimationError,
    refetch: refetchEstimation,
  } = useBridgeEstimation({
    fromNetwork: formatChainIdForTradesilvania(swapDirection.from),
    toNetwork: formatChainIdForTradesilvania(swapDirection.to),
    amount,
    enabled:
      !!amount && parseFloat(amount) > 0 && !isBelowMinimum && !isAboveMaximum,
  });

  // Countdown timer for estimation expiration
  const { timeLeft, formattedTime, isExpired } = useCountdown(
    estimation?.fixed?.expireIn || 0,
    estimation?.fixed?.rateId // Add rateId as a dependency to reset timer on new estimation
  );

  // Auto-refetch when estimation expires
  useEffect(() => {
    if (
      isExpired &&
      estimation &&
      amount &&
      parseFloat(amount) > 0 &&
      !isEstimating
    ) {
      refetchEstimation();
    }
  }, [isExpired, estimation, amount, refetchEstimation, isEstimating]);

  // Bridge swap handling
  const bridgeSwap = useBridgeSwap();
  const bridgeTransaction = useBridgeTransactionHooks();

  // Wallet connection state
  const fromWalletConnected = bridgeWallet.walletStates[swapDirection.from];
  const toWalletConnected = bridgeWallet.walletStates[swapDirection.to];

  const canSwap =
    fromWalletConnected &&
    toWalletConnected &&
    amount &&
    estimation &&
    !hasInsufficientBalance &&
    !isBelowMinimum &&
    !isAboveMaximum &&
    !isExpired;

  // Get button text based on current state
  const getButtonText = () => {
    if (!fromWalletConnected || !toWalletConnected) {
      return "Connect Wallets";
    }

    if (!amount) {
      return "Enter Amount";
    }

    if (hasInsufficientBalance) {
      return "Insufficient Balance";
    }

    if (isBelowMinimum) {
      return `Minimum ${minAmount} A1X`;
    }

    if (isAboveMaximum) {
      return `Maximum ${maxAmount} A1X`;
    }

    if (limitsError || estimationError) {
      return "Fix Error Above";
    }

    if (!estimation && isEstimating) {
      return "Getting Quote...";
    }

    if (!estimation) {
      return "Enter Amount";
    }

    if (isExpired) {
      return "Quote Expired";
    }

    return "Bridge A1X";
  };

  // Check if button should be disabled
  const isButtonDisabled = () => {
    // If wallets are connected but there's an issue with the transaction
    if (fromWalletConnected && toWalletConnected) {
      return (
        !amount ||
        !estimation ||
        hasInsufficientBalance ||
        isBelowMinimum ||
        isAboveMaximum ||
        isExpired ||
        !!estimationError ||
        !!limitsError
      );
    }

    // If wallets aren't connected, button is enabled for connection
    return false;
  };

  // Handlers
  const handleSwapDirection = useCallback(() => {
    setSwapDirection((prev) => ({
      from: prev.to,
      to: prev.from,
    }));
  }, []);

  const handleAmountChange = useCallback((value: string) => {
    setAmount(value);
  }, []);

  const handlePercentageClick = useCallback(
    (percentage: number) => {
      const currentBalance = getA1XBalance(swapDirection.from);
      if (currentBalance) {
        const balanceNum = parseFloat(currentBalance);
        const newAmount = ((balanceNum * percentage) / 100).toString();
        setAmount(newAmount);
      }
    },
    [getA1XBalance, swapDirection.from]
  );

  const handleSwap = useCallback(async () => {
    if (!canSwap || !estimation) return;

    const fromAddress =
      swapDirection.from === "solana"
        ? solanaPublicKey?.toBase58() || ""
        : mvxAddress || "";

    const toAddress =
      swapDirection.to === "solana"
        ? solanaPublicKey?.toBase58() || ""
        : mvxAddress || "";

    try {
      // 1. Fetch the order
      const result = await bridgeSwap.mutateAsync({
        fromNetwork: formatChainIdForTradesilvania(swapDirection.from),
        toNetwork: formatChainIdForTradesilvania(swapDirection.to),
        amount,
        fromAddress,
        toAddress,
        rateId: estimation.fixed.rateId,
      });
      // console.log("result", result);

      // const result = {
      //   id: "b5279072-cdc0-4388-b7bb-dcea9513ed14",
      //   status: "waiting",
      //   expireAt: 1752666230331,
      //   payInAddress: "EVFAZiuph5joePVvDLwmThNsX36sNakgNGS5fBXpQt24",
      //   rateType: "fixed",
      //   amountFrom: "4001",
      //   amountTo: "3924.918733",
      //   rate: "0.98098443",
      //   baseRate: "0.98098443",
      // };

      // 2. Execute the transaction with the payinAddress
      if (result.payInAddress) {
        try {
          await bridgeTransaction.executeBridgeTransaction({
            fromNetwork: swapDirection.from,
            payInAddress: result.payInAddress,
            amount,
          });

          // Store the order ID if transaction was successful
          if (swapDirection.from === "solana") {
            toast.success(
              "Bridge transaction successful! Your A1X tokens are being transferred."
            );
          }
        } catch (txError) {
          // Transaction failed, but order was created
          const errorMessage =
            txError instanceof Error ? txError.message : "Unknown error";
          toast.error(`Order created but transaction failed: ${errorMessage}`);
          return;
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      toast.error(`Transaction failed: ${errorMessage}`);
    }
  }, [
    canSwap,
    estimation,
    swapDirection,
    amount,
    solanaPublicKey,
    mvxAddress,
    bridgeSwap,
    bridgeTransaction,
  ]);

  const handleRetryEstimation = useCallback(() => {
    window.location.reload();
  }, []);

  return (
    <div className="font-roboto">
      <div className="max-w-lg mx-auto pt-4 sm:pt-8 pb-8 sm:pb-12 px-3 sm:px-4">
        <BridgeHeader />

        {/* Main Bridge Card */}
        <Card className="bg-neutral-900 border border-neutral-700 shadow-2xl">
          <CardBody className="p-4 sm:p-6">
            {/* From Network */}
            <BridgeNetworkCard
              network={swapDirection.from}
              type="from"
              isConnected={fromWalletConnected}
              walletAddress={
                swapDirection.from === "solana"
                  ? solanaPublicKey?.toBase58() || ""
                  : mvxAddress || ""
              }
              currentBalance={getA1XBalance(swapDirection.from)}
              isBalanceLoading={isBalanceLoading(swapDirection.from)}
              onDisconnect={() => handleDisconnect(swapDirection.from)}
              amountValue={amount}
              onAmountChange={handleAmountChange}
              onPercentageClick={handlePercentageClick}
              hasInsufficientBalance={hasInsufficientBalance}
              isBelowMinimum={isBelowMinimum}
              isAboveMaximum={isAboveMaximum}
              limits={limits}
              limitsError={limitsError}
            />

            {/* Swap Direction Button */}
            <div className="flex justify-center -my-2 sm:-my-3 relative z-20">
              <Button
                isIconOnly
                variant="flat"
                className="bg-neutral-800 hover:bg-neutral-700 border border-neutral-600 rounded-full w-10 h-10 sm:w-12 sm:h-12"
                onPress={handleSwapDirection}
              >
                <ArrowUpDown size={18} className="text-gray-400 sm:hidden" />
                <ArrowUpDown
                  size={20}
                  className="text-gray-400 hidden sm:block"
                />
              </Button>
            </div>

            {/* To Network */}
            <BridgeNetworkCard
              network={swapDirection.to}
              type="to"
              amount={estimation?.variable.amountTo}
              isConnected={toWalletConnected}
              walletAddress={
                swapDirection.to === "solana"
                  ? solanaPublicKey?.toBase58() || ""
                  : mvxAddress || ""
              }
              currentBalance={getA1XBalance(swapDirection.to)}
              isBalanceLoading={isBalanceLoading(swapDirection.to)}
              onDisconnect={() => handleDisconnect(swapDirection.to)}
              isEstimating={isEstimating}
            />

            {/* Error Display */}
            {estimationError && !isBelowMinimum && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 my-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-red-500 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-red-500 font-semibold text-sm mb-1">
                      Something went wrong
                    </h4>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {estimationError instanceof Error
                        ? estimationError.message
                        : String(estimationError)}
                    </p>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-1">
                    <button
                      onClick={handleRetryEstimation}
                      className="border-none text-gray-500 bg-neutral-900 hover:text-gray-300 hover:bg-gray-800/50 p-1.5 rounded-md transition-colors duration-200"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        const currentAmount = amount;
                        setAmount("");
                        setTimeout(() => setAmount(currentAmount), 100);
                      }}
                      className="text-gray-500 border-none bg-gray-900 hover:text-gray-300 hover:bg-gray-900/50 p-1.5 rounded-md transition-colors duration-200"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Limits Information */}
            {limits && !estimationError && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-neutral-800/60 rounded-lg p-2 sm:p-3 text-xs text-gray-400 space-y-1 rounded-t-none mx-2 sm:mx-3 pt-3 sm:pt-4 -mt-2"
              >
                <div className="flex justify-between">
                  <span>Minimum amount:</span>
                  <span className="text-white">
                    {limits.assetFrom.amountMin} A1X
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Maximum amount:</span>
                  <span className="text-white">
                    {limits.assetFrom.amountMax} A1X
                  </span>
                </div>
              </motion.div>
            )}

            {/* Exchange Details */}
            {estimation && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 sm:mt-6 bg-neutral-800 rounded-xl p-3 sm:p-4 border border-neutral-700"
              >
                <div className="flex items-center justify-between text-sm mb-3">
                  <span className="text-gray-400 font-roboto">
                    Exchange Rate
                  </span>
                  <span className="text-white font-medium font-roboto">
                    1 A1X = {estimation.variable.rate} A1X
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400 font-roboto">Expires in</span>
                  <span
                    className={`font-medium font-roboto ${
                      isExpired
                        ? "text-red-400"
                        : timeLeft < 60000
                        ? "text-yellow-400"
                        : "text-white"
                    }`}
                  >
                    {isExpired ? "Expired" : formattedTime}
                  </span>
                </div>
              </motion.div>
            )}

            {/* Action Button */}
            <Button
              className={`w-full mt-4 sm:mt-6 h-12 sm:h-14 text-base sm:text-lg font-semibold border-0 font-roboto text-center ${
                !fromWalletConnected || !toWalletConnected || canSwap
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-neutral-700 text-gray-400"
              }`}
              size="lg"
              onPress={() => {
                const requiredNetworks: NetworkType[] = [
                  swapDirection.from,
                  swapDirection.to,
                ];
                const allConnected =
                  bridgeWallet.checkConnectionsAndPrompt(requiredNetworks);

                if (allConnected) {
                  handleSwap();
                }
              }}
              isDisabled={isButtonDisabled()}
              isLoading={bridgeSwap.isPending || bridgeTransaction.isLoading}
            >
              {getButtonText()}
            </Button>
          </CardBody>
        </Card>

        {/* Powered by Tradesilvania */}
        <div className="text-center mt-4 text-sm text-gray-400">
          Powered by Tradesilvania
        </div>

        {/* Transaction History */}
        {/* <BridgeHistoryCard /> */}

        {/* Modals */}
        <BridgeWalletModal
          isOpen={bridgeWallet.isModalOpen}
          onClose={bridgeWallet.closeModal}
          requiredNetworks={[swapDirection.from, swapDirection.to]}
          onAllConnected={bridgeWallet.handleAllConnected}
        />
      </div>
    </div>
  );
};
